import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

/**
 * Tenant data model
 */
export const Tenant = {
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: null,
  ssn: "", // Last 4 digits only for security
  emergencyContact: {
    name: "",
    relationship: "",
    phone: "",
  },
  employment: {
    employer: "",
    position: "",
    monthlyIncome: 0,
    startDate: null,
  },
  rentalHistory: {
    previousLandlords: [],
    evictions: false,
    bankruptcies: false,
  },
  creditScore: 0,
  status: "active", // 'active', 'inactive', 'evicted', 'moved_out'
  propertyId: "",
  unitNumber: "",
  leaseStart: null,
  leaseEnd: null,
  monthlyRent: 0, // What the tenant actually pays
  rentHistory: [], // Array of rent changes for this tenant
  securityDeposit: 0,
  lateFeeAmount: 0,
  gracePeriod: 3, // days
  paymentMethod: "ach", // 'ach', 'credit_card', 'check', 'cash'
  // New rent due date and notification settings
  rentDueDay: 1, // Day of month when rent is due (1-31)
  rentReminderSettings: [
    { daysBeforeDue: 7, enabled: true, channel: "email" }, // Default: 7 days before
    { daysBeforeDue: 1, enabled: true, channel: "email" }, // Default: 1 day before
  ], // Array of reminder settings
  lateRentNotificationSettings: [
    { daysAfterDue: 1, enabled: true, channel: "email" }, // Default: 1 day after
  ], // Array of late notification settings
  createdAt: null,
  updatedAt: null,
};

/**
 * Payment data model
 */
export const Payment = {
  id: "",
  tenantId: "",
  propertyId: "",
  amount: 0,
  type: "rent", // 'rent', 'late_fee', 'security_deposit', 'other'
  status: "pending", // 'pending', 'completed', 'failed', 'refunded'
  dueDate: null,
  paidDate: null,
  lateFeeAmount: 0,
  method: "ach", // 'ach', 'credit_card', 'check', 'cash'
  transactionId: "",
  notes: "",
  createdAt: null,
};

/** Firestore Timestamp, plain {seconds}, ISO string, Date, or null - for sorting tenant lists. */
function tenantCreatedAtMillis(tenant) {
  const v = tenant?.createdAt;
  if (v == null) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v === "object" && typeof v.seconds === "number") {
    const nanos = typeof v.nanoseconds === "number" ? v.nanoseconds : 0;
    return v.seconds * 1000 + nanos / 1e6;
  }
  if (typeof v === "string") {
    const ms = Date.parse(v);
    return Number.isFinite(ms) ? ms : 0;
  }
  if (v instanceof Date) return v.getTime();
  return 0;
}

/**
 * List all tenants for the current user
 */
export async function listTenants() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Equality-only queries (no orderBy) avoid composite indexes on
    // ownerId+createdAt / landlordId+createdAt. Previously, a missing index made
    // Promise.all reject; the catch returned { tenants: [] } with no UI error.
    const tenantsRef = collection(db, "tenants");
    const [ownerSnap, landlordSnap] = await Promise.all([
      getDocs(query(tenantsRef, where("ownerId", "==", user.uid))),
      getDocs(query(tenantsRef, where("landlordId", "==", user.uid))),
    ]);

    const seenIds = new Set();
    const tenants = [];
    for (const snap of [ownerSnap, landlordSnap]) {
      for (const doc of snap.docs) {
        if (seenIds.has(doc.id)) continue;
        const data = doc.data();
        // Applicants / lease-up pipeline records live in `tenants` with is_draft.
        // Keep them off the rent roll; Lease-Ups loads them separately.
        if (data?.is_draft === true) continue;
        seenIds.add(doc.id);
        tenants.push({ id: doc.id, ...data });
      }
    }
    tenants.sort((a, b) => tenantCreatedAtMillis(b) - tenantCreatedAtMillis(a));

    return {
      tenants,
      total: tenants.length,
    };
  } catch (error) {
    console.error("Error listing tenants:", error);
    throw error;
  }
}

/**
 * Create a new tenant
 */
export async function createTenant(tenantData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Set default notification settings if not provided
    const tenantWithDefaults = {
      ...tenantData,
      rentDueDay: tenantData.rentDueDay || 1,
      rentReminderSettings: tenantData.rentReminderSettings || [
        { daysBeforeDue: 7, enabled: true, channel: "email" },
        { daysBeforeDue: 1, enabled: true, channel: "email" },
      ],
      lateRentNotificationSettings: tenantData.lateRentNotificationSettings || [
        { daysAfterDue: 1, enabled: true, channel: "email" },
      ],
      welcomeEmailSent:
        tenantData.welcomeEmailSent !== undefined
          ? tenantData.welcomeEmailSent
          : false,
      ownerId: user.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Add to Firestore
    const tenantsRef = collection(db, "tenants");
    const docRef = await addDoc(tenantsRef, tenantWithDefaults);

    // Return the created tenant
    const newTenant = {
      id: docRef.id,
      ...tenantWithDefaults,
    };

    return {
      success: true,
      tenant: newTenant,
      message: "Tenant created successfully",
    };
  } catch (error) {
    console.error("Error creating tenant:", error);
    throw error;
  }
}

/**
 * Update an existing tenant
 */
export async function updateTenant(tenantId, updates) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Ensure welcomeEmailSent is set to false if not present
    const tenantRef = doc(db, "tenants", tenantId);
    const updatesWithDefaults = {
      ...updates,
      updatedAt: new Date(),
    };

    // If welcomeEmailSent is not in the updates, check if it exists in the tenant document
    // If it doesn't exist, set it to false
    if (updates.welcomeEmailSent === undefined) {
      const tenantDoc = await getDoc(tenantRef);

      if (tenantDoc.exists()) {
        const tenantData = tenantDoc.data();
        if (tenantData.welcomeEmailSent === undefined) {
          updatesWithDefaults.welcomeEmailSent = false;
        }
      } else {
        // If tenant doesn't exist, set it to false
        updatesWithDefaults.welcomeEmailSent = false;
      }
    }

    // Update in Firestore
    await updateDoc(tenantRef, updatesWithDefaults);

    return {
      success: true,
      tenant: {
        id: tenantId,
        ...updates,
        updatedAt: new Date(),
      },
      message: "Tenant updated successfully",
    };
  } catch (error) {
    console.error("Error updating tenant:", error);
    throw error;
  }
}

/**
 * Mark a tenant's rent as paid
 */
export async function markPaid(tenantId, payment) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // TODO(api): implement server call
    // For now, return mock response
    return {
      success: true,
      payment: {
        id: `payment_${Date.now()}`,
        tenantId,
        ...payment,
        status: "completed",
        paidDate: new Date(),
        createdAt: new Date(),
      },
      message: "Payment recorded successfully",
    };
  } catch (error) {
    console.error("Error marking payment:", error);
    throw error;
  }
}

/**
 * Get tenant by ID
 */
export async function getTenant(tenantId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get tenant document from Firestore
    const tenantRef = doc(db, "tenants", tenantId);
    const tenantSnap = await getDoc(tenantRef);

    if (!tenantSnap.exists()) {
      throw new Error("Tenant not found");
    }

    const tenantData = tenantSnap.data();

    // Verify the tenant belongs to the current user.
    // Support legacy docs that stored landlordId instead of ownerId.
    if (tenantData.ownerId !== user.uid && tenantData.landlordId !== user.uid) {
      throw new Error("Unauthorized access to tenant data");
    }

    return {
      id: tenantSnap.id,
      ...tenantData,
    };
  } catch (error) {
    console.error("Error getting tenant:", error);
    throw error;
  }
}

/**
 * Delete tenant by ID
 */
export async function deleteTenant(tenantId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Delete from Firestore
    const tenantRef = doc(db, "tenants", tenantId);
    await deleteDoc(tenantRef);

    return {
      success: true,
      message: "Tenant deleted successfully",
    };
  } catch (error) {
    console.error("Error deleting tenant:", error);
    throw error;
  }
}

/**
 * Update tenant rent
 */
export async function updateTenantRent(
  tenantId,
  newRent,
  reason = "rent_adjustment"
) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const tenantRef = doc(db, "tenants", tenantId);
    const tenantDoc = await getDoc(tenantRef);

    if (!tenantDoc.exists()) {
      throw new Error("Tenant not found");
    }

    const tenantData = tenantDoc.data();
    const rentHistoryEntry = {
      date: new Date(),
      previousRent: tenantData.monthlyRent,
      newRent: newRent,
      reason: reason,
      updatedBy: user.uid,
    };

    await updateDoc(tenantRef, {
      monthlyRent: newRent,
      rentHistory: [...(tenantData.rentHistory || []), rentHistoryEntry],
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Tenant rent updated successfully",
    };
  } catch (error) {
    console.error("Error updating tenant rent:", error);
    throw error;
  }
}

/**
 * Update tenant rent due day
 */
export async function updateTenantRentDueDay(tenantId, rentDueDay) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    if (rentDueDay < 1 || rentDueDay > 31) {
      throw new Error("Rent due day must be between 1 and 31");
    }

    const tenantRef = doc(db, "tenants", tenantId);
    await updateDoc(tenantRef, {
      rentDueDay: rentDueDay,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Rent due day updated successfully",
    };
  } catch (error) {
    console.error("Error updating rent due day:", error);
    throw error;
  }
}

/**
 * Update tenant rent reminder settings
 */
export async function updateTenantRentReminders(tenantId, reminderSettings) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    /*
    if (!user) {
      throw new Error('User not authenticated');
    }
    */

    // Validate reminder settings
    if (!Array.isArray(reminderSettings)) {
      throw new Error("Reminder settings must be an array");
    }

    const tenantRef = doc(db, "tenants", tenantId);
    await updateDoc(tenantRef, {
      rentReminderSettings: reminderSettings,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Rent reminder settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating rent reminder settings:", error);
    throw error;
  }
}

/**
 * Update tenant late rent notification settings
 */
export async function updateTenantLateNotifications(
  tenantId,
  lateNotificationSettings
) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Validate late notification settings
    if (!Array.isArray(lateNotificationSettings)) {
      throw new Error("Late notification settings must be an array");
    }

    const tenantRef = doc(db, "tenants", tenantId);
    await updateDoc(tenantRef, {
      lateRentNotificationSettings: lateNotificationSettings,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: "Late rent notification settings updated successfully",
    };
  } catch (error) {
    console.error("Error updating late rent notification settings:", error);
    throw error;
  }
}

/**
 * Get tenant rent history
 */
export async function getTenantRentHistory(tenantId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const tenantRef = doc(db, "tenants", tenantId);
    const tenantDoc = await getDoc(tenantRef);

    if (!tenantDoc.exists()) {
      throw new Error("Tenant not found");
    }

    const tenantData = tenantDoc.data();
    return tenantData.rentHistory || [];
  } catch (error) {
    console.error("Error getting tenant rent history:", error);
    throw error;
  }
}

/**
 * Get tenants by rent range
 */
export async function getTenantsByRentRange(minRent, maxRent) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const tenantsRef = collection(db, "tenants");
    const q = query(
      tenantsRef,
      where("ownerId", "==", user.uid),
      where("monthlyRent", ">=", minRent),
      where("monthlyRent", "<=", maxRent),
      orderBy("monthlyRent", "desc")
    );

    const querySnapshot = await getDocs(q);
    const tenants = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return {
      tenants,
      total: tenants.length,
    };
  } catch (error) {
    console.error("Error getting tenants by rent range:", error);
    throw error;
  }
}
